import { Badge, Card } from "@exhousing/design-system";
import { Check, ListTodo } from "lucide-react";
import { useState } from "react";

import { AccountDrawer } from "@/components/crm/AccountDrawer";
import { LeadDrawer } from "@/components/crm/LeadDrawer";
import { useAccounts, useCompleteNote, useLeads, useOpenTasks } from "@/hooks/api/useCrm";
import type { components } from "@/types/api";

// Namespaced because notes_management also defines a NoteResponse.
type Note = components["schemas"]["app__domains__crm_management__schemas__NoteResponse"];

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

type Bucket = "overdue" | "week" | "later";

function bucketOf(due: string | null): Bucket {
  if (!due) return "later";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(due);
  d.setHours(0, 0, 0, 0);
  const diffDays = (d.getTime() - today.getTime()) / 86_400_000;
  if (diffDays < 0) return "overdue";
  if (diffDays <= 7) return "week";
  return "later";
}

const BUCKETS: { key: Bucket; label: string; tone?: "warning" }[] = [
  { key: "overdue", label: "Overdue", tone: "warning" },
  { key: "week", label: "This week" },
  { key: "later", label: "Later" },
];

export function FollowUpsList() {
  const { data: tasks } = useOpenTasks();
  const { data: leads } = useLeads();
  const { data: accounts } = useAccounts();
  const complete = useCompleteNote();
  const [lead, setLead] = useState<string | null>(null);
  const [account, setAccount] = useState<string | null>(null);

  const subjectName = (t: Note): string => {
    if (t.subject_type === "lead") {
      const l = (leads ?? []).find((x) => x.id === t.subject_id);
      return l ? [l.first_name, l.last_name].filter(Boolean).join(" ") || "Unnamed lead" : "Lead";
    }
    const a = (accounts ?? []).find((x) => x.id === t.subject_id);
    return a?.name ?? "Account";
  };

  const open = (t: Note) =>
    t.subject_type === "lead" ? setLead(t.subject_id) : setAccount(t.subject_id);

  const list = tasks ?? [];

  if (list.length === 0) {
    return (
      <Card className="flex flex-col items-center gap-2 py-12 text-center">
        <ListTodo className="size-6 text-muted-foreground" />
        <p className="text-sm font-medium">You&rsquo;re all caught up</p>
        <p className="text-sm text-muted-foreground">No open follow-ups across your leads and accounts.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {BUCKETS.map(({ key, label, tone }) => {
        const items = list.filter((t) => bucketOf(t.due_date) === key);
        if (items.length === 0) return null;
        return (
          <div key={key} className="space-y-2">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium">{label}</h3>
              <Badge tone={tone === "warning" ? "warning" : "neutral"}>{items.length}</Badge>
            </div>
            <Card className="divide-y divide-border/60 p-0">
              {items.map((t) => (
                <div key={t.id} className="flex items-center gap-3 px-4 py-3">
                  <button
                    onClick={() => complete.mutate(t.id)}
                    disabled={complete.isPending}
                    aria-label="Mark done"
                    className="grid size-5 shrink-0 place-items-center rounded-full border border-input transition-colors hover:border-primary"
                  >
                    <Check className="size-3 opacity-0 hover:opacity-100" />
                  </button>
                  <button onClick={() => open(t)} className="min-w-0 flex-1 text-left">
                    <p className="truncate text-sm">{t.body}</p>
                    <p className="text-xs text-muted-foreground">
                      {subjectName(t)} · {t.subject_type === "lead" ? "Lead" : "Account"}
                    </p>
                  </button>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                    {t.due_date ? fmtDate(t.due_date) : "—"}
                  </span>
                </div>
              ))}
            </Card>
          </div>
        );
      })}

      <LeadDrawer leadId={lead} onClose={() => setLead(null)} />
      <AccountDrawer accountId={account} onClose={() => setAccount(null)} />
    </div>
  );
}
